import { api } from './api';

export interface AmbulanceSearchData {
  pickupLocation: string;
  destination: string;
  urgency: 'priority' | 'regular';
  requiredEquipment?: string;
  originCoordinates?: { lat: number; lng: number };
  destinationCoordinates?: { lat: number; lng: number };
}

export interface AmbulanceProvider {
  provider: {
    id: string;
    companyName: string;
    phone?: string;
    coordinates?: any;
  };
  pricing: {
    basePrice: number;
    emergencySurge: number;
    total: number;
    breakdown: string;
    currency: string;
  };
  distance?: number;
  estimatedTime?: number;
}

export interface AmbulanceBookingData {
  providerId: string;
  caseId?: string;
  tripType: 'PRIORITY' | 'REGULAR';
  originAddress: string;
  destinationAddress: string;
  originCoordinates?: { lat: number; lng: number };
  destinationCoordinates?: { lat: number; lng: number };
  accessNotes: string;
  onSiteContact: {
    name: string;
    phone: string;
    shareWithProvider: boolean;
  };
  ambulanceType: string;
  specialRequests?: string;
  scheduledDateTime?: Date;
}

export interface BankTransferPaymentData {
  amount: number;
  transferReference: string;
  transferTime?: string;
  notes?: string;
  receiptImageUrl?: string;
  // Keep legacy fields for backward compatibility but make them optional
  bankName?: string;
  accountHolderName?: string;
  accountNumberLast?: string;
}

export interface AmbulanceBookingListOptions {
  page?: number;
  limit?: number;
  status?: string;
  sortBy?: 'createdAt' | 'scheduledDateTime';
  sortOrder?: 'asc' | 'desc';
}

export interface AmbulanceBookingListResponse {
  bookings: AmbulanceBookingItem[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface AmbulanceBookingItem {
  id: string;
  bookingId: string;
  route: string;
  providerName: string;
  status: string;
  createdAt: string;
  scheduledDateTime?: string;
  tripType?: string;
  ambulanceType?: string;
  pricing?: any;
  paymentStatus?: string;
  caseNumber: string;
  deceasedName?: string;
  workflow: {
    currentStep: string;
    nextStep: string;
    isCompleted: boolean;
    progress: number;
  };
}

export interface BookingStatus {
  booking: {
    id: string;
    status: string;
    serviceType: string;
    scheduledDateTime: Date;
    originAddress: string;
    destinationAddress: string;
    tripType: string;
    ambulanceType: string;
    pricing: any;
  };
  provider?: {
    id: string;
    companyName: string;
    phone: string;
  };
  case?: {
    id: string;
    caseNumber: string;
    deceasedName: string;
  };
  payment?: any;
  workflow: {
    currentStep: string;
    nextStep: string;
    isCompleted: boolean;
    progress: number;
  };
}

export const ambulanceBookingService = {
  /**
   * Search for available ambulance providers with real-time pricing
   */
  async searchAmbulances(searchData: AmbulanceSearchData): Promise<AmbulanceProvider[]> {
    const response = await api.post('/ambulance-booking/search', searchData);
    return response.data.data.ambulances;
  },

  /**
   * Create ambulance booking (pending payment)
   */
  async createBooking(bookingData: Omit<AmbulanceBookingData, 'caseId'>, caseId?: string): Promise<any> {
    const payload = caseId ? { ...bookingData, caseId } : bookingData;
    const response = await api.post('/ambulance-booking/create', payload);
    return response.data.data;
  },

  /**
   * Submit bank transfer payment for booking
   */
  async submitPayment(bookingId: string, paymentData: BankTransferPaymentData): Promise<any> {
    const response = await api.post(`/ambulance-booking/${bookingId}/payment`, paymentData);
    return response.data.data;
  },

  /**
   * Get booking status with payment information
   */
  async getBookingStatus(bookingId: string): Promise<BookingStatus> {
    const response = await api.get(`/ambulance-booking/${bookingId}/status`);
    return response.data.data;
  },

  /**
   * Get user's ambulance bookings with pagination and filtering
   */
  async getBookingsList(options?: AmbulanceBookingListOptions): Promise<AmbulanceBookingListResponse> {
    const params = new URLSearchParams();

    if (options?.page) params.append('page', options.page.toString());
    if (options?.limit) params.append('limit', options.limit.toString());
    if (options?.status) params.append('status', options.status);
    if (options?.sortBy) params.append('sortBy', options.sortBy);
    if (options?.sortOrder) params.append('sortOrder', options.sortOrder);

    const queryString = params.toString();
    const url = `/ambulance-booking/list${queryString ? `?${queryString}` : ''}`;

    const response = await api.get(url);
    return response.data.data;
  },

  /**
   * Get user's ambulance bookings (legacy method for compatibility)
   */
  async getUserBookings(): Promise<any> {
    const response = await api.get('/ambulance-booking/my-bookings');
    return response.data.data;
  },

  /**
   * Delete an ambulance booking
   */
  async deleteBooking(bookingId: string): Promise<void> {
    await api.delete(`/ambulance-booking/${bookingId}`);
  },
};